import { getCarById, getTrackById } from '../../content';
import type { GameController } from '../../core/GameController';
import { displaySpeed, formatRaceTime } from '../../core/format';
import type { GameSnapshot } from '../../core/types';
import { formatInputCode } from '../../input/InputManager';
import { ActionButton } from '../components/MenuFrame';

export function RaceOverlay({ controller, snapshot }: { controller: GameController; snapshot: GameSnapshot }) {
  const race = snapshot.race;
  if (!race) return null;
  const track = getTrackById(snapshot.selectedTrackId);
  const car = getCarById(snapshot.selectedCarId);
  const speed = displaySpeed(race.telemetry.speedMps, snapshot.settings.speedUnit);
  const boostActive = race.telemetry.boostAmount > 0.05;
  const sliding = race.telemetry.sliding > 0.38;
  const restartKey = formatInputCode(snapshot.settings.bindings.restart[0] ?? '');
  const resetKey = formatInputCode(snapshot.settings.bindings.reset[0] ?? '');
  const pauseKey = formatInputCode(snapshot.settings.bindings.pause[0] ?? '');

  return (
    <div className={`race-overlay screen-layer ${boostActive ? 'is-boosting' : ''}`}>
      <header className="race-hud race-hud--top">
        <div className="race-course-tag">
          <span>{track.difficulty}</span>
          <b>{track.name}</b>
          <small>{car.name}</small>
        </div>
        <div className="race-timer">
          <small>Race time</small>
          <strong>{formatRaceTime(race.timeMs)}</strong>
          <span className="race-timer__line" />
        </div>
        <div className="race-best">
          <small>Personal best</small>
          <b>{formatRaceTime(race.bestTimeMs)}</b>
          <span>{race.bestTimeMs === null ? 'Set the benchmark' : 'Chase the record'}</span>
        </div>
      </header>

      <div className="checkpoint-progress">
        <span>Checkpoint</span>
        <b>{Math.min(race.checkpointIndex + 1, race.checkpointCount)} <small>/ {race.checkpointCount}</small></b>
        <div className="checkpoint-progress__pips">
          {Array.from({ length: race.checkpointCount }, (_, index) => (
            <i key={index} className={index < race.checkpointIndex ? 'is-passed' : index === race.checkpointIndex ? 'is-next' : ''} />
          ))}
        </div>
      </div>

      <div className={`speedometer ${sliding ? 'is-sliding' : ''}`}>
        <div className="speedometer__arc" aria-hidden="true"><i /></div>
        <strong>{speed}</strong>
        <span>{snapshot.settings.speedUnit === 'mph' ? 'MPH' : 'KM/H'}</span>
        <small>{sliding ? 'TIRE SLIP' : boostActive ? 'BOOST' : race.telemetry.grounded ? 'GRIP' : 'AIR'}</small>
      </div>

      <div className="race-controls-hint">
        <span><kbd>{restartKey}</kbd> Restart</span>
        <span><kbd>{resetKey}</kbd> Reset</span>
        <span><kbd>{pauseKey}</kbd> Pause</span>
      </div>

      {race.countdownText && (
        <div className={`countdown countdown--${race.countdownText.toLowerCase()}`} key={race.countdownText}>
          <span>{race.countdownText}</span>
          <i />
        </div>
      )}

      {(race.notice || race.missingCheckpointWarning) && (
        <div className={`race-notice ${race.missingCheckpointWarning ? 'race-notice--warning' : ''}`}>
          <span aria-hidden="true">{race.missingCheckpointWarning ? '!' : '✓'}</span>
          <b>{race.missingCheckpointWarning ? 'CHECKPOINTS MISSING' : race.notice}</b>
        </div>
      )}

      {boostActive && <div className="boost-vignette" aria-hidden="true"><span>BOOST</span></div>}
      {snapshot.paused && <PauseMenu controller={controller} snapshot={snapshot} />}
    </div>
  );
}

function PauseMenu({ controller, snapshot }: { controller: GameController; snapshot: GameSnapshot }) {
  const track = getTrackById(snapshot.selectedTrackId);
  const pauseKey = formatInputCode(snapshot.settings.bindings.pause[0] ?? '');
  return (
    <div className="modal-backdrop pause-backdrop">
      <section className="pause-menu" role="dialog" aria-modal="true" aria-label="Race paused">
        <header>
          <p className="eyebrow">Timer stopped</p>
          <h2>Race Paused</h2>
          <span>{track.name}</span>
        </header>
        <div className="pause-menu__actions">
          <ActionButton autoFocus variant="primary" icon="▶" label="Resume" detail="Return to the course" onClick={() => controller.resumeRace()} />
          <ActionButton icon="↻" label="Restart Race" detail="Return to the starting grid" onClick={() => controller.restartRace()} />
          <ActionButton icon="⚙" label="Settings" detail="The timer remains paused" onClick={() => controller.openPauseSettings()} />
          <ActionButton icon="⌁" label="Return to Track Select" onClick={() => controller.returnToTrackSelect()} />
          <ActionButton icon="⌂" label="Main Menu" variant="quiet" onClick={() => controller.showMainMenu()} />
        </div>
        <footer>Press <kbd>{pauseKey}</kbd> to resume</footer>
      </section>
    </div>
  );
}
